import React, { useEffect, useState } from 'react';
import { useTranslate } from 'react-polyglot';
import { useQuery } from '@tanstack/react-query';

import styles from './DogSummaryCard.module.scss';
import { useAuth } from '../../../../Auth';
import { useAxios } from '../../../../AxiosHandler';
import Skeleton from '../../../Shared/Skeleton/Skeleton';

// The «Tispe» / «Hannhund» card on the partner request page: the matched dog
// in two label / value columns (GET /api/statistics/dog/:id/summary). What is
// below the facts (remove button, search when there is no match) comes from
// the page as children.

function DogSummaryCard(props: DogSummaryCardProps) {
    const t = useTranslate();
    const auth = useAuth();
    const axios = useAxios().axios;
    const [copied, setCopied] = useState<string | null>(null);
    const [hittaExists, setHittaExists] = useState<boolean>(false);

    const k = (key: string, options?: any) => t("selectedrequestbreeding.dogcard." + key, options);
    const animalId = props.match?.ANIMAL_ID;

    const { data: summary, isLoading: summaryLoading } = useQuery<any>({
        queryKey: ['dog-summary', animalId],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + animalId + "/summary",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data),
        enabled: animalId != undefined,
        staleTime: 5 * 60 * 1000,
    });

    // As on the dog page: only when the council has Hitta Alghund enabled
    const hittaEnabled = auth.user?.userdata?.COUNCIL_ACCESS?.find((c: any) => c.BREED_ID == auth.selectedBreed)?.HITTAALGHUND_ENABLED == 1;
    useEffect(() => {
        if (!hittaEnabled || !props.match?.REGNO) {
            setHittaExists(false);
            return;
        }
        axios.single({
            method: 'get',
            url: '/api/dogs/check/hittaalghund/' + encodeURIComponent(props.match.REGNO),
            responseType: 'json',
        }).then((res: any) => setHittaExists(res.data?.exists === true)).catch(() => setHittaExists(false));
    }, [props.match?.REGNO, hittaEnabled]);

    function copy(value: string, key: string) {
        navigator.clipboard.writeText(value ?? "");
        setCopied(key);
        setTimeout(() => setCopied(null), 1500);
    }

    function age(dateBirth: string | undefined) {
        if (!dateBirth) return "–";
        const birth = new Date(dateBirth);
        const now = new Date();
        let years = now.getFullYear() - birth.getFullYear();
        if (now.getMonth() < birth.getMonth() || (now.getMonth() == birth.getMonth() && now.getDate() < birth.getDate())) years--;
        return years + " " + k("years");
    }

    // «1. premie / 81 p (2024)»
    function bestTest(test: { count: number, best: any } | undefined) {
        const best = test?.best;
        if (!best) return "–";
        const prize = best.prize != null ? k("prize", { prize: best.prize }) : k("noprize");
        const points = best.points != null ? " / " + k("points", { points: best.points }) : "";
        const year = best.date ? " (" + new Date(best.date).getFullYear() + ")" : "";
        return prize + points + year;
    }

    const female = props.role == "female";
    const s = summary;
    const offspring = s?.offspring;

    // Values from the summary show a placeholder until it has arrived
    const waiting = props.loading || summaryLoading;
    let placeholder = 0;
    function field(label: string, value: React.ReactNode, fromSummary: boolean = true) {
        const width = [70, 45, 90, 55, 80, 35][placeholder++ % 6];
        return (
            <>
                <dt>{label}</dt>
                <dd>{fromSummary && waiting ? <Skeleton width={width} height={12} /> : value ?? "–"}</dd>
            </>
        );
    }

    function copyButton(value: string, key: string) {
        return (
            <button type="button" className={styles.copy} onClick={() => copy(value, key)} title={k("copy")}>
                {copied == key ? "✓" : "⎘"}
            </button>
        );
    }

    return (
        <section className={styles.dogcard}>
            <div className={styles.avatar + " " + (female ? styles.female : styles.male)}>
                <span>{female ? "T" : "H"}</span>
                {animalId != undefined && (
                    <img
                        src={`/api/dogs/photo/${animalId}`}
                        alt=""
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                )}
            </div>
            <h2>{k(female ? "female" : "male")}</h2>
            {props.loading && (
                // The request itself is still loading: the card's layout only
                <div className={styles.columns} aria-busy="true">
                    <dl>
                        {field(k("fields.name"), null)}
                        {field(k("fields.regnr"), null)}
                        {field(k("fields.age"), null)}
                        {field(k("fields.hd"), null)}
                        {field(k("fields.hdindex"), null)}
                    </dl>
                    <dl>
                        {field(k("fields.show"), null)}
                        {field(k("fields.offspring"), null)}
                        {field(k("fields.hdoffspring"), null)}
                        {field(k("fields.glaukom"), null)}
                    </dl>
                </div>
            )}
            {!props.loading && props.match?.REGNO != undefined && (
                <>
                    <div className={styles.columns}>
                        <dl>
                            {field(k("fields.name"), <>{props.match.NAME}{copyButton(props.match.NAME, "name")}</>, false)}
                            {field(k("fields.regnr"), <>
                                <a className={styles.link} href={"/hunder/" + animalId} target="_blank" rel="noreferrer">{props.match.REGNO}</a>
                                {copyButton(props.match.REGNO, "regnr")}
                            </>, false)}
                            {field(k("fields.age"), age(s?.DATE_BIRTH))}
                            {field(k("fields.hd"), s?.HD)}
                            {field(k("fields.ad"), s?.AD)}
                            {field(k("fields.hdindex"), s?.HD_INDEX != null
                                ? <a className={styles.link} href={"/hunder/" + animalId} target="_blank" rel="noreferrer">{s.HD_INDEX}</a>
                                : null)}
                            {props.testStarts == undefined && !waiting
                                ? field(k("fields.tests"), <Skeleton width={30} height={12} />, false)
                                : field(k("fields.tests"), props.testStarts)}
                        </dl>
                        <dl>
                            {waiting && field(k("fields.besttest"), null)}
                            {(s?.tests?.loose?.count ?? 0) > 0 && field(k("fields.bestloose"), bestTest(s.tests.loose))}
                            {(s?.tests?.leash?.count ?? 0) > 0 && field(k("fields.bestleash"), bestTest(s.tests.leash))}
                            {field(k("fields.show"), s?.bestShow ? t("selectedrequestbreeding.combination.grades." + s.bestShow.grade) : null)}
                            {field(k("fields.offspring"), offspring?.total)}
                            {field(k("fields.hdoffspring"), offspring && offspring.total > 0
                                ? Math.round(100 * offspring.hdTested / offspring.total) + " % (" + offspring.hdTested + "/" + offspring.total + ")"
                                : null)}
                            {(waiting || s?.genes?.glaukom != null) && field(k("fields.glaukom"), s?.genes?.glaukom)}
                            {(waiting || s?.genes?.ataksi != null) && field(k("fields.ataksi"), s?.genes?.ataksi)}
                            {s?.genes?.dvergvekst != null && field(k("fields.dvergvekst"), s.genes.dvergvekst)}
                        </dl>
                    </div>
                    <div className={styles.links}>
                        <a
                            className={styles.link}
                            href={"https://www.dogweb.no/hundedatabase/dog?id=" + props.match.REGNO}
                            target="_blank"
                            rel="noreferrer"
                        >{k("dogweb")} &#8599;</a>
                        {hittaExists && (
                            <a
                                className={styles.link}
                                href={"https://www.hittaalghund.se/dog.asp?Regnummer=" + props.match.REGNO}
                                target="_blank"
                                rel="noreferrer"
                            >{k("hitta")} &#8599;</a>
                        )}
                    </div>
                </>
            )}
            {props.children}
        </section>
    );
}

class DogSummaryCardProps {
    role!: "female" | "male";
    // DOGMATCH / MALEDOGMATCH of the request: { ANIMAL_ID, NAME, REGNO }
    match?: any;
    // All test starts (leash, loose, hare, bird ...), counted by the page;
    // undefined while they load
    testStarts?: number;
    // The request is still loading: show the card's layout with placeholders
    loading?: boolean;
    children?: React.ReactNode;
}

export default DogSummaryCard;
